<template>
  <div class="timeline-map-page">
    <div class="timeline-map-page__view-switch">
      <button
          type="button"
          :class="[
          'timeline-map-page__view-switch-btn',          
          'timeline-map-page__view-switch-btn--map',
          { 'timeline-map-page__view-switch-btn--active': currentView === 'map' }
        ]"
          @click="setView('map')"
          v-if="store.showMap"
      >
        {{ store.translations.map }}
      </button>

      <button
          type="button"
          :class="[
          'timeline-map-page__view-switch-btn',
          'timeline-map-page__view-switch-btn--timeline',
          { 'timeline-map-page__view-switch-btn--active': currentView === 'timeline' }
        ]"
          @click="setView('timeline')"
          v-if="store.showMomentsList || store.showMap"
      >
        {{ store.translations.timeline }}
      </button>

      <button
          type="button"
          :class="[
          'timeline-map-page__view-switch-btn',
          'timeline-map-page__view-switch-btn--moments-list',
          { 'timeline-map-page__view-switch-btn--active': currentView === 'momentsList' }
        ]"
          @click="setView('momentsList')"
          v-if="store.showMomentsList"
      >
        {{ store.translations.momentsList }}
      </button>
    </div>

    <component :is="currentComponent" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";

import TimelineMM from "./components/timeline/TimelineMM.vue";
import TimelineJVM from "./components/timeline/TimelineJVM.vue";
import TimelineVRAK from "./components/timeline/TimelineVRAK.vue";
import MapVRAK from './components/map/MapVRAK.vue'
import MomentsList from "./components/moments-list/MomentsList.vue";

const store = useTimelineMapStore();
const route = useRoute()
const router = useRouter()

function coerceView(raw: unknown): 'timeline' | 'map' | 'momentsList' | undefined {
  const v = Array.isArray(raw) ? raw[0] : raw
  return v === 'map' || v === 'timeline' || v === 'momentsList' ? v : undefined
}

function setView(view: 'timeline' | 'map' | 'momentsList') {
  router.replace({
    path: route.params.id ? `/${route.params.id}` : '/',
    query: { ...route.query, view }
  })
}

function enforceAllowedView() {
  const v = coerceView(route.query.view)

  const isAllowed =
    v == null ||
    v === 'timeline' ||
    (v === 'map' && store.showMap) ||
    (v === 'momentsList' && store.showMomentsList)

  if (!isAllowed) {
    router.replace({
      path: route.params.id ? `/${route.params.id}` : '/',
      query: { ...route.query, view: undefined },
    })
  }
}

function defaultMapWhenEnabled() {
  const v = coerceView(route.query.view)
  if (store.showMap && v == null) {
    setView('map')
  }
}

onMounted(() => {
  if (store.isMM || store.isVRAK) {
    document.body.classList.add('header-locked');
  }
  if (store.isVRAK) {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }
  enforceAllowedView()
  defaultMapWhenEnabled()
})

onUnmounted(() => {
  if (store.isMM || store.isVRAK) {
    document.body.classList.remove('header-locked');
  }
})

watch(() => [store.showMap, store.showMomentsList], () => {
  if (store.isVRAK) {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }
  enforceAllowedView()
  defaultMapWhenEnabled()
})

// currentView - reads direct from URL
const currentView = computed<'timeline' | 'map' | 'momentsList'>(() => {
  const v = coerceView(route.query.view)
  if (v === 'momentsList') { return 'momentsList' }
  return v === 'map' ? 'map' : 'timeline'
})

const timelineComponent = computed(() => {
  const mapping = { jvm: TimelineJVM, mm: TimelineMM, vrak: TimelineVRAK }
  if (store.isJVM) return mapping.jvm
  if (store.isMM) return mapping.mm
  if (store.isVRAK) return mapping.vrak
  return TimelineMM
})

const mapComponent = computed(() => {
  const mapping = { vrak: MapVRAK }
  if (store.isVRAK) return mapping.vrak
  return TimelineVRAK
})

const currentComponent = computed(() => {
  if (currentView.value === 'map') {
    return mapComponent.value
  }

  if (currentView.value === 'momentsList') {
    return MomentsList
  }

  return timelineComponent.value
})
</script>
